'use client';
import styles from './notification-modal.module.css';
import Modal from '@/components/modal/modal';
import { useEffect, useState, useTransition } from 'react';
import {
	enableDisableNewsletterAgain,
	getUserNotifications,
	updateUserNotifications,
} from '@/lib/user';
import { useSession } from 'next-auth/react';
import { toast } from 'sonner';

export type UserNotifications = {
	new: boolean;
	news: boolean;
	investigators: boolean;
	watch: boolean;
};

type NotificationModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

export default function NotificationModal({
	isOpen,
	onClose,
}: NotificationModalProps) {
	const { data: session } = useSession();

	const [notifications, setNotification] = useState<UserNotifications>({
		new: false,
		news: false,
		investigators: false,
		watch: false,
	});

	const [isDisabled, setIsDisabled] = useState(false);

	const [what, setWhat] = useState<
		'idle' | 'fetch' | 'updateNotifications' | 'updateNewsLetter'
	>('idle');
	const [isPending, startTransition] = useTransition();

	const handleCheckboxChange = (key: keyof typeof notifications) => {
		setNotification((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	async function handleNotification() {
		startTransition(async () => {
			setWhat('updateNotifications');

			await updateUserNotifications(session?.user.id!, notifications);
		});
		setWhat('idle');
		onClose();
		toast.success('Notifications Updated Successfully!');
	}

	async function handleSusbscription() {
		startTransition(async () => {
			setWhat('updateNewsLetter');
			await enableDisableNewsletterAgain(session?.user.id!, 'delete');
		});
		setWhat('idle');
	}

	useEffect(() => {
		if (!session?.user.id) return;
		else {
			if (isOpen)
				startTransition(async () => {
					setWhat('fetch');
					const data = await getUserNotifications(session.user.id, true);

					if (data.success) {
						setIsDisabled(data.isDisabled);
						setNotification(data.notifications);
					}
				});
			setWhat('idle');
		}
	}, [isOpen, getUserNotifications, session?.user.id]);

	return (
		<Modal isOpen={isOpen} onClose={onClose} auto>
			<Modal.Title>
				<div className={styles['dialog-title']}>Get updated on new trials</div>
			</Modal.Title>
			<Modal.Body>
				{isDisabled ? (
					<p className={styles['newsletter-subscription']}>
						You have previously unsubscribed from emails from
						newsletter@scitrials.org. Click below to start receiving our
						newsletter again.
					</p>
				) : (
					<div className={styles['dialog-body']}>
						<ul className={styles['body-li']}>
							<li>Find out about new trials immediately by email</li>
							<li>Get updates to trials you are interested in</li>
							<li>Learn about the latest news in SCI trials</li>
							<li>We can tell you about trials that you match</li>
						</ul>

						<div className={styles.checkboxes}>
							{[
								{ key: 'news', label: 'Latest News' },
								{ key: 'new', label: 'New Trials' },
								{ key: 'watch', label: 'Trials you are following' },
								{ key: 'investigators', label: 'Trials that you match' },
							].map(({ key, label }) => (
								<div key={key} className={styles['checkbox-container']}>
									<input
										type='checkbox'
										id={key}
										name={key}
										checked={notifications[key as keyof typeof notifications]}
										onChange={() =>
											handleCheckboxChange(key as keyof typeof notifications)
										}
										disabled={isPending}
									/>
									<label htmlFor={key}>{label}</label>
								</div>
							))}
						</div>
					</div>
				)}
			</Modal.Body>
			<Modal.Footer>
				{isDisabled ? (
					<div className={styles.footer}>
						<button
							type='submit'
							className={styles.save}
							onClick={handleSusbscription}
							disabled={isPending}
						>
							{isPending && what === 'updateNewsLetter'
								? 'Subscribing...'
								: 'Subscribe Again'}
						</button>
					</div>
				) : (
					<div className={styles.footer}>
						<div className={styles['btn-actions']}>
							<button
								type='submit'
								className={styles.save}
								onClick={handleNotification}
								disabled={isPending}
							>
								{isPending && what === 'updateNotifications'
									? 'Saving...'
									: 'Save'}
							</button>
							<button type='button' className={styles.cancel} onClick={onClose}>
								Cancel
							</button>
						</div>
						<p>We aren't going to spam you. Opt out anytime.</p>
					</div>
				)}
			</Modal.Footer>
		</Modal>
	);
}
